import { useState, useEffect, useCallback } from "react";
import { useAuth } from "@/App";
import GlassCard from "@/components/GlassCard";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { LineChart, Line, AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer } from "recharts";
import { Brain, Heart, Moon, Activity, Sparkles, Loader2 } from "lucide-react";
import { toast } from "sonner";

function ScoreCircle({ score, size = 160 }) {
  const radius = (size - 16) / 2;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference - (score / 100) * circumference;
  return (
    <div className="relative score-circle" style={{ width: size, height: size }}>
      <svg width={size} height={size} className="-rotate-90">
        <circle cx={size/2} cy={size/2} r={radius} fill="none" stroke="rgba(255,255,255,0.05)" strokeWidth="8" />
        <circle cx={size/2} cy={size/2} r={radius} fill="none" stroke="url(#neonGrad)" strokeWidth="8"
          strokeLinecap="round" strokeDasharray={circumference} strokeDashoffset={offset}
          className="transition-all duration-1000 ease-out"
        />
        <defs>
          <linearGradient id="neonGrad" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor="#00f260" />
            <stop offset="100%" stopColor="#05cb51" />
          </linearGradient>
        </defs>
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className="font-heading text-5xl font-bold text-white neon-text">{score}</span>
        <span className="text-xs text-gray-400 uppercase tracking-wider">/ 100</span>
      </div>
    </div>
  );
}

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="glass-card !p-3 !rounded-lg text-xs">
      <p className="text-gray-400">{label}</p>
      {payload.map((p, i) => (
        <p key={i} style={{ color: p.color }} className="font-medium">{p.name}: {typeof p.value === 'number' ? p.value.toFixed(1) : p.value}</p>
      ))}
    </div>
  );
};

export default function LongevityPage() {
  const { api } = useAuth();
  const [metrics, setMetrics] = useState([]);
  const [aiRec, setAiRec] = useState("");
  const [aiLoading, setAiLoading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [savingMetrics, setSavingMetrics] = useState(false);
  const todayDate = new Date().toISOString().slice(0, 10);
  const [metricsForm, setMetricsForm] = useState({
    date: todayDate,
    longevity_score: "",
    biological_age: "",
    hrv: "",
    sleep_hours: "",
    sleep_quality: "",
    stress_level: "",
    vo2max: ""
  });

  const fetchMetrics = useCallback(() => {
    api().get("/metrics?days=7").then(r => {
      setMetrics(r.data.sort((a, b) => a.date.localeCompare(b.date)));
      setLoading(false);
    }).catch(() => setLoading(false));
  }, [api]);

  useEffect(() => {
    fetchMetrics();
  }, [fetchMetrics]);

  const fetchAI = useCallback(() => {
    setAiLoading(true);
    api().post("/ai/recommendation", { context: "longevity" })
      .then(r => setAiRec(r.data.recommendation))
      .catch(() => setAiRec("Nem sikerült betölteni az AI ajánlást."))
      .finally(() => setAiLoading(false));
  }, [api]);

  useEffect(() => {
    fetchAI();
  }, [fetchAI]);

  const updateMetricsField = (field, value) => {
    setMetricsForm(prev => ({ ...prev, [field]: value }));
  };

  const handleMetricsSubmit = async (e) => {
    e.preventDefault();
    const payload = {
      date: metricsForm.date
    };
    ["longevity_score", "biological_age", "hrv", "sleep_hours", "sleep_quality", "stress_level", "vo2max"].forEach(field => {
      if (metricsForm[field] !== "" && metricsForm[field] !== null) {
        payload[field] = Number(metricsForm[field]);
      }
    });
    if (Object.keys(payload).length === 1) {
      toast.error("Adj meg legalább egy metrikát.");
      return;
    }
    setSavingMetrics(true);
    try {
      const res = await api().post("/metrics", payload);
      const saved = res.data;
      setMetrics(prev => {
        const next = prev.filter(m => m.date !== saved.date);
        next.push(saved);
        return next.sort((a, b) => a.date.localeCompare(b.date));
      });
      toast.success("Metrikák mentve!");
    } catch {
      toast.error("Nem sikerült menteni a metrikákat");
    } finally {
      setSavingMetrics(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="w-8 h-8 border-2 border-[#00f260] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const latest = metrics[metrics.length - 1] || {};
  const chartData = metrics.map(m => ({
    date: m.date?.slice(5) || "",
    HRV: m.hrv,
    Alvás: m.sleep_quality,
    Stressz: m.stress_level,
    Longevity: m.longevity_score
  }));

  return (
    <div className="space-y-6 md:pt-20" data-testid="longevity-page">
      {/* Hero */}
      <GlassCard glow className="text-center py-8 fade-in-up" testId="longevity-hero">
        <p className="text-xs text-gray-400 uppercase tracking-widest mb-4">Longevity Pontszám</p>
        <div className="flex justify-center mb-4">
          <ScoreCircle score={latest.longevity_score || 87} />
        </div>
        <div className="flex items-center justify-center gap-6 mt-4">
          <div>
            <p className="text-xs text-gray-400">Biológiai kor</p>
            <p className="font-heading text-2xl font-bold text-[#00f260]">
              {latest.biological_age?.toFixed(1) || "29.4"}
            </p>
          </div>
          <div className="w-px h-10 bg-white/10" />
          <div>
            <p className="text-xs text-gray-400">VO2max</p>
            <p className="font-heading text-2xl font-bold text-white">
              {latest.vo2max?.toFixed(1) || "48.5"}
            </p>
          </div>
        </div>
      </GlassCard>

      {/* Metric Grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <MetricCard icon={Activity} label="VO2max" value={`${latest.vo2max?.toFixed(1) || "48.5"}`} unit="ml/kg/min" delay="1" />
        <MetricCard icon={Moon} label="Alvás minőség" value={`${latest.sleep_quality || 85}`} unit="%" delay="2" />
        <MetricCard icon={Brain} label="Stressz szint" value={`${latest.stress_level || 32}`} unit="%" delay="3" />
        <MetricCard icon={Heart} label="HRV" value={`${latest.hrv?.toFixed(0) || "72"}`} unit="ms" delay="4" />
      </div>

      {/* Metrics Input */}
      <GlassCard testId="metrics-input-card" className="fade-in-up fade-in-up-2">
        <div className="flex items-center justify-between mb-4">
          <p className="text-xs text-gray-400 uppercase tracking-wider">Napi metrikák frissítése</p>
          <span className="text-[10px] text-gray-500">Valós idejű</span>
        </div>
        <form onSubmit={handleMetricsSubmit} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div className="space-y-2">
              <Label className="text-gray-400 text-xs">Dátum</Label>
              <Input
                data-testid="metrics-date-input"
                type="date"
                value={metricsForm.date}
                onChange={(e) => updateMetricsField("date", e.target.value)}
                className="bg-black/20 border-white/10 text-white focus:border-[#00f260] focus:ring-[#00f260] rounded-xl h-11"
                required
              />
            </div>
            <div className="space-y-2">
              <Label className="text-gray-400 text-xs">Longevity pont</Label>
              <Input
                data-testid="metrics-longevity-input"
                type="number"
                min="0"
                max="100"
                value={metricsForm.longevity_score}
                onChange={(e) => updateMetricsField("longevity_score", e.target.value)}
                placeholder="87"
                className="bg-black/20 border-white/10 text-white placeholder:text-gray-500 focus:border-[#00f260] focus:ring-[#00f260] rounded-xl h-11"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-gray-400 text-xs">Biológiai kor</Label>
              <Input
                data-testid="metrics-biological-age-input"
                type="number"
                min="0"
                step="0.1"
                value={metricsForm.biological_age}
                onChange={(e) => updateMetricsField("biological_age", e.target.value)}
                placeholder="29.4"
                className="bg-black/20 border-white/10 text-white placeholder:text-gray-500 focus:border-[#00f260] focus:ring-[#00f260] rounded-xl h-11"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
            <div className="space-y-2">
              <Label className="text-gray-400 text-xs">HRV (ms)</Label>
              <Input
                data-testid="metrics-hrv-input"
                type="number"
                min="0"
                step="1"
                value={metricsForm.hrv}
                onChange={(e) => updateMetricsField("hrv", e.target.value)}
                placeholder="72"
                className="bg-black/20 border-white/10 text-white placeholder:text-gray-500 focus:border-[#00f260] focus:ring-[#00f260] rounded-xl h-11"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-gray-400 text-xs">Alvás (óra)</Label>
              <Input
                data-testid="metrics-sleep-hours-input"
                type="number"
                min="0"
                step="0.1"
                value={metricsForm.sleep_hours}
                onChange={(e) => updateMetricsField("sleep_hours", e.target.value)}
                placeholder="7.5"
                className="bg-black/20 border-white/10 text-white placeholder:text-gray-500 focus:border-[#00f260] focus:ring-[#00f260] rounded-xl h-11"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-gray-400 text-xs">Alvás minőség (%)</Label>
              <Input
                data-testid="metrics-sleep-quality-input"
                type="number"
                min="0"
                max="100"
                step="1"
                value={metricsForm.sleep_quality}
                onChange={(e) => updateMetricsField("sleep_quality", e.target.value)}
                placeholder="85"
                className="bg-black/20 border-white/10 text-white placeholder:text-gray-500 focus:border-[#00f260] focus:ring-[#00f260] rounded-xl h-11"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-gray-400 text-xs">Stressz szint (%)</Label>
              <Input
                data-testid="metrics-stress-input"
                type="number"
                min="0"
                max="100"
                step="1"
                value={metricsForm.stress_level}
                onChange={(e) => updateMetricsField("stress_level", e.target.value)}
                placeholder="32"
                className="bg-black/20 border-white/10 text-white placeholder:text-gray-500 focus:border-[#00f260] focus:ring-[#00f260] rounded-xl h-11"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 items-end">
            <div className="space-y-2">
              <Label className="text-gray-400 text-xs">VO2max</Label>
              <Input
                data-testid="metrics-vo2max-input"
                type="number"
                min="0"
                step="0.1"
                value={metricsForm.vo2max}
                onChange={(e) => updateMetricsField("vo2max", e.target.value)}
                placeholder="48.5"
                className="bg-black/20 border-white/10 text-white placeholder:text-gray-500 focus:border-[#00f260] focus:ring-[#00f260] rounded-xl h-11"
              />
            </div>
            <button
              data-testid="metrics-submit-btn"
              type="submit"
              disabled={savingMetrics}
              className="neon-btn w-full text-sm flex items-center justify-center gap-2"
            >
              {savingMetrics ? <Loader2 size={16} className="animate-spin" /> : null}
              Metrikák mentése
            </button>
          </div>
        </form>
      </GlassCard>

      {/* Recovery Trend Chart */}
      <GlassCard testId="longevity-trend-chart" className="fade-in-up fade-in-up-3">
        <p className="text-xs text-gray-400 uppercase tracking-wider mb-4">Regeneráció trend (7 nap)</p>
        <div className="h-48">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={chartData}>
              <defs>
                <linearGradient id="areaGreen" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#00f260" stopOpacity={0.3} />
                  <stop offset="95%" stopColor="#00f260" stopOpacity={0} />
                </linearGradient>
              </defs>
              <XAxis dataKey="date" tick={{ fill: "#6b7280", fontSize: 11 }} axisLine={false} tickLine={false} />
              <YAxis hide domain={[0, 100]} />
              <Tooltip content={<CustomTooltip />} />
              <Area type="monotone" dataKey="Longevity" stroke="#00f260" fill="url(#areaGreen)" strokeWidth={2} />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </GlassCard>

      {/* HRV Trend */}
      <GlassCard testId="hrv-trend-chart" className="fade-in-up fade-in-up-4">
        <p className="text-xs text-gray-400 uppercase tracking-wider mb-4">HRV & Alvás trend</p>
        <div className="h-48">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={chartData}>
              <XAxis dataKey="date" tick={{ fill: "#6b7280", fontSize: 11 }} axisLine={false} tickLine={false} />
              <YAxis hide />
              <Tooltip content={<CustomTooltip />} />
              <Line type="monotone" dataKey="HRV" stroke="#00f260" strokeWidth={2} dot={false} />
              <Line type="monotone" dataKey="Alvás" stroke="#ffc107" strokeWidth={2} dot={false} />
            </LineChart>
          </ResponsiveContainer>
        </div>
        <div className="flex gap-4 mt-2">
          <span className="flex items-center gap-1 text-xs text-gray-400"><span className="w-3 h-0.5 bg-[#00f260] rounded" />HRV</span>
          <span className="flex items-center gap-1 text-xs text-gray-400"><span className="w-3 h-0.5 bg-[#ffc107] rounded" />Alvás</span>
        </div>
      </GlassCard>

      {/* AI Recommendation */}
      <GlassCard glow testId="ai-recommendation-card" className="fade-in-up fade-in-up-5">
        <div className="flex items-center gap-2 mb-3">
          <Sparkles size={16} className="text-[#00f260]" />
          <p className="text-xs text-gray-400 uppercase tracking-wider">AI Ajánlás</p>
        </div>
        {aiLoading ? (
          <div className="flex items-center gap-2 text-gray-400 text-sm">
            <Loader2 size={16} className="animate-spin" />
            AI elemzés folyamatban...
          </div>
        ) : (
          <p className="text-white/90 text-sm leading-relaxed">{aiRec}</p>
        )}
        <button
          data-testid="optimize-recovery-btn"
          onClick={() => { window.location.href = "/recovery"; }}
          className="neon-btn text-xs py-2 px-5 mt-4"
        >
          Regeneráció optimalizálása
        </button>
      </GlassCard>
    </div>
  );
}

function MetricCard({ icon: Icon, label, value, unit, delay }) {
  return (
    <GlassCard className={`text-center fade-in-up fade-in-up-${delay}`}>
      <Icon size={20} className="text-[#00f260] mx-auto mb-2" strokeWidth={1.5} />
      <p className="text-[10px] text-gray-400 uppercase tracking-wider mb-1">{label}</p>
      <p className="font-heading text-2xl font-bold text-white">{value}</p>
      <p className="text-[10px] text-gray-500">{unit}</p>
    </GlassCard>
  );
}
